import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useResearchCapability } from "@/hooks/useResearchCapability";
import { format } from "date-fns";
import { Search, Network, ArrowRight, ArrowLeft, ExternalLink, X, Activity, Sparkles, ChevronDown, Info, Mail, Calendar, FileText, Mic, Database, Globe, FolderOpen, Brain, GitBranch } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import ResearchKGHealth from "./ResearchKGHealth";
import { nodeLabel } from "@/components/research-hub/labels";

type Node = {
  id: string;
  node_type: string;
  external_id: string | null;
  external_table: string | null;
  label: string;
  properties: any;
  embedding_model: string | null;
  created_at: string;
  updated_at: string;
};

type Edge = {
  id: string;
  from_node_id: string;
  to_node_id: string;
  edge_type: string;
  weight: number | null;
  evidence_source_id: string | null;
  properties: any;
  valid_from: string | null;
  valid_to: string | null;
};

const FONT_STACK = {
  fontFamily: "'Manrope', system-ui, sans-serif",
} as React.CSSProperties;

const HEADING_STACK = {
  fontFamily: "'Sora', 'Manrope', system-ui, sans-serif",
} as React.CSSProperties;

export default function ResearchKG() {
  const { data: canRead, isLoading: capLoading } = useResearchCapability("read");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("");
  const [selected, setSelected] = useState<Node | null>(null);
  const [view, setView] = useState<"browse" | "health">("browse");

  const { data: nodes, isLoading } = useQuery({
    queryKey: ["research_kg_nodes", "all-pages"],
    queryFn: async () => {
      const all: Node[] = [];
      const pageSize = 1000;
      for (let from = 0; ; from += pageSize) {
        const { data, error } = await supabase
          .from("research_kg_nodes")
          .select("id,node_type,external_id,external_table,label,properties,embedding_model,created_at,updated_at")
          .order("updated_at", { ascending: false })
          .range(from, from + pageSize - 1);
        if (error) throw error;
        if (!data || data.length === 0) break;
        all.push(...(data as Node[]));
        if (data.length < pageSize) break;
      }
      return all;
    },
    enabled: !!canRead,
  });

  const { data: exactNodeCount } = useQuery({
    queryKey: ["research_kg_nodes", "exact-count"],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("research_kg_nodes")
        .select("id", { count: "exact", head: true });
      if (error) throw error;
      return count ?? 0;
    },
    enabled: !!canRead,
  });


  const typeCounts = useMemo(() => {
    const m = new Map<string, number>();
    (nodes ?? []).forEach((n) => m.set(n.node_type, (m.get(n.node_type) ?? 0) + 1));
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [nodes]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (nodes ?? []).filter((n) => {
      if (typeFilter && n.node_type !== typeFilter) return false;
      if (!q) return true;
      return (
        n.label.toLowerCase().includes(q) ||
        (n.external_id ?? "").toLowerCase().includes(q) ||
        (n.external_table ?? "").toLowerCase().includes(q)
      );
    });
  }, [nodes, search, typeFilter]);

  // Edges for selected node — paginate fully (no cap) so high-degree nodes show every claim
  const { data: edges } = useQuery({
    queryKey: ["research_kg_edges", selected?.id],
    queryFn: async () => {
      const all: Edge[] = [];
      const pageSize = 1000;
      for (let from = 0; ; from += pageSize) {
        const { data, error } = await supabase
          .from("research_kg_edges")
          .select("*")
          .or(`from_node_id.eq.${selected!.id},to_node_id.eq.${selected!.id}`)
          .range(from, from + pageSize - 1);
        if (error) throw error;
        if (!data || data.length === 0) break;
        all.push(...(data as Edge[]));
        if (data.length < pageSize) break;
      }
      return all;
    },
    enabled: !!selected,
  });

  // Most connected nodes — paginate the entire edge table so stats scale with the LLM-training-size graph
  const { data: connectionStats } = useQuery({
    queryKey: ["research_kg_connection_stats", "all-pages"],
    queryFn: async () => {
      const counts = new Map<string, number>();
      const pageSize = 1000;
      for (let from = 0; ; from += pageSize) {
        const { data, error } = await supabase
          .from("research_kg_edges")
          .select("from_node_id,to_node_id")
          .range(from, from + pageSize - 1);
        if (error) throw error;
        if (!data || data.length === 0) break;
        (data as any[]).forEach((e) => {
          counts.set(e.from_node_id, (counts.get(e.from_node_id) ?? 0) + 1);
          counts.set(e.to_node_id, (counts.get(e.to_node_id) ?? 0) + 1);
        });
        if (data.length < pageSize) break;
      }
      return counts;
    },
    enabled: !!canRead,
  });

  // Exact edge count via head:true — accurate regardless of pagination state
  const { data: exactEdgeCount } = useQuery({
    queryKey: ["research_kg_edges", "exact-count"],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("research_kg_edges")
        .select("id", { count: "exact", head: true });
      if (error) throw error;
      return count ?? 0;
    },
    enabled: !!canRead,
  });


  const nodeMap = useMemo(() => {
    const m = new Map<string, Node>();
    (nodes ?? []).forEach((n) => m.set(n.id, n));
    return m;
  }, [nodes]);

  const topConnected = useMemo(() => {
    if (!connectionStats || !nodes) return [];
    return [...connectionStats.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([id, count]) => ({ node: nodeMap.get(id), count }))
      .filter((x) => x.node) as { node: Node; count: number }[];
  }, [connectionStats, nodes, nodeMap]);

  const recentlyUpdated = useMemo(() => (nodes ?? []).slice(0, 8), [nodes]);

  const totalEdges = useMemo(() => {
    if (!connectionStats) return 0;
    let sum = 0;
    connectionStats.forEach((v) => (sum += v));
    return Math.round(sum / 2);
  }, [connectionStats]);

  if (capLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (!canRead) return <p className="text-sm text-muted-foreground">Requires read capability.</p>;

  return (
    <div style={FONT_STACK} className="-mx-4 sm:-mx-6 -my-4 sm:-my-6">
      {/* Header band */}
      <div className="px-4 sm:px-6 py-5 border-b border-border bg-gradient-to-br from-[hsl(214_53%_15%)] to-[hsl(214_53%_22%)] text-white">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#a8c0d6] mb-1">
              <Network className="h-3.5 w-3.5" />
              Research · Knowledge Graph
            </div>
            <h1 style={HEADING_STACK} className="text-2xl font-semibold leading-tight">
              The structured memory of your research
            </h1>
            <p className="text-sm text-[#cfdaea] mt-1 max-w-2xl">
              Entities (funds, people, themes, sectors) and the sourced claims that link them. Search an entity to see what's known about it and where the claims came from.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant={view === "browse" ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setView("browse")}
              className={view === "browse" ? "bg-white text-[#1B365D] hover:bg-white/90" : "text-white hover:bg-white/10 hover:text-white"}
            >
              <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Explore
            </Button>
            <Button
              variant={view === "health" ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setView("health")}
              className={view === "health" ? "bg-white text-[#1B365D] hover:bg-white/90" : "text-white hover:bg-white/10 hover:text-white"}
            >
              <Activity className="h-3.5 w-3.5 mr-1.5" /> Health
            </Button>
          </div>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
          <KpiCard label="Entities" value={exactNodeCount ?? nodes?.length ?? 0} hint="things the graph knows about" />
          <KpiCard label="Claims" value={exactEdgeCount ?? totalEdges} hint="sourced relationships" />
          <KpiCard label="Entity types" value={typeCounts.length} hint="funds, people, themes…" />
          <KpiCard
            label="Last update"
            value={recentlyUpdated[0] ? format(new Date(recentlyUpdated[0].updated_at), "dd/MM/yyyy") : "—"}
            hint="most recent claim ingested"
          />
        </div>
      </div>

      {view === "health" ? (
        <div className="p-4 sm:p-6">
          <ResearchKGHealth />
        </div>
      ) : (
        <div className="grid grid-cols-12 min-h-[calc(100vh-260px)]">
          {/* Left rail */}
          <aside className="col-span-12 md:col-span-3 lg:col-span-3 xl:col-span-2 border-r border-border bg-muted/30 flex flex-col">
            <div className="p-3 border-b border-border">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search entities…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-8 h-9 bg-background"
                />
              </div>
            </div>
            <div className="p-3 border-b border-border">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                Filter by type
              </div>
              <div className="space-y-0.5">
                <TypeRow
                  label="All entities"
                  count={exactNodeCount ?? nodes?.length ?? 0}
                  active={!typeFilter}
                  onClick={() => setTypeFilter("")}
                />
                {typeCounts.map(([t, c]) => (
                  <TypeRow
                    key={t}
                    label={t}
                    count={c}
                    active={typeFilter === t}
                    onClick={() => setTypeFilter(t)}
                  />
                ))}
              </div>
            </div>
            <ScrollArea className="flex-1">
              <div className="p-2">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground px-2 py-1.5">
                  {filtered.length} entities
                </div>
                {isLoading ? (
                  <p className="text-xs text-muted-foreground p-2">Loading…</p>
                ) : (
                  filtered.slice(0, 200).map((n) => (
                    <button
                      key={n.id}
                      onClick={() => setSelected(n)}
                      className={`w-full text-left px-2 py-1.5 rounded-md text-sm transition-colors ${
                        selected?.id === n.id
                          ? "bg-[#1B365D] text-white"
                          : "hover:bg-background text-foreground"
                      }`}
                    >
                      <div className="truncate font-medium">{n.label}</div>
                      <div
                        className={`text-[10px] uppercase tracking-wide truncate ${
                          selected?.id === n.id ? "text-white/70" : "text-muted-foreground"
                        }`}
                      >
                        {n.node_type}
                      </div>
                    </button>
                  ))
                )}
                {filtered.length > 200 && (
                  <p className="text-[10px] text-muted-foreground p-2">
                    Showing first 200. Refine search to narrow.
                  </p>
                )}
              </div>
            </ScrollArea>
          </aside>

          {/* Canvas */}
          <main className="col-span-12 md:col-span-9 lg:col-span-9 xl:col-span-10 bg-background">
            {selected ? (
              <EntityDetail
                node={selected}
                edges={edges ?? []}
                nodeMap={nodeMap}
                onPivot={(n) => setSelected(n)}
                onClose={() => setSelected(null)}
              />
            ) : (
              <Overview
                topConnected={topConnected}
                recentlyUpdated={recentlyUpdated}
                onPick={(n) => setSelected(n)}
                typeCounts={typeCounts}
              />
            )}
          </main>
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, value, hint }: { label: string; value: string | number; hint: string }) {
  return (
    <div className="rounded-lg bg-white/10 backdrop-blur px-3 py-2.5 border border-white/10">
      <div className="text-[10px] uppercase tracking-wider text-[#a8c0d6]">{label}</div>
      <div style={HEADING_STACK} className="text-xl font-semibold mt-0.5">{value}</div>
      <div className="text-[10px] text-[#a8c0d6] mt-0.5 truncate">{hint}</div>
    </div>
  );
}

function TypeRow({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center justify-between px-2 py-1.5 rounded-md text-xs transition-colors ${
        active ? "bg-[#3D8C7C] text-white" : "hover:bg-background text-foreground"
      }`}
    >
      <span className="truncate font-medium">{label}</span>
      <span className={`tabular-nums text-[10px] ${active ? "text-white/80" : "text-muted-foreground"}`}>
        {count}
      </span>
    </button>
  );
}

function Overview({
  topConnected,
  recentlyUpdated,
  onPick,
  typeCounts,
}: {
  topConnected: { node: any; count: number }[];
  recentlyUpdated: any[];
  onPick: (n: any) => void;
  typeCounts: [string, number][];
}) {
  return (
    <div className="p-6 max-w-6xl">
      <div className="mb-6">
        <h2 style={HEADING_STACK} className="text-lg font-semibold">
          Pick an entity to begin
        </h2>
        <p className="text-sm text-muted-foreground">
          Search on the left, or jump straight into one of the most-connected or most-recently-updated entities below.
        </p>
      </div>

      <HowItWorks />



      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section>
          <div className="flex items-center gap-2 mb-3">
            <Network className="h-4 w-4 text-[#3D8C7C]" />
            <h3 style={HEADING_STACK} className="text-sm font-semibold uppercase tracking-wider">
              Most connected
            </h3>
          </div>
          <Card>
            <CardContent className="p-2">
              {topConnected.length === 0 ? (
                <p className="text-xs text-muted-foreground p-3">No edges yet.</p>
              ) : (
                topConnected.map(({ node, count }) => (
                  <button
                    key={node.id}
                    onClick={() => onPick(node)}
                    className="w-full flex items-center justify-between gap-3 px-3 py-2 rounded-md hover:bg-muted/50 text-left"
                  >
                    <div className="min-w-0">
                      <div className="font-medium text-sm truncate">{node.label}</div>
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                        {node.node_type}
                      </div>
                    </div>
                    <Badge variant="secondary" className="bg-[#3D8C7C]/10 text-[#3D8C7C] border-0">
                      {count} links
                    </Badge>
                  </button>
                ))
              )}
            </CardContent>
          </Card>
        </section>

        <section>
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="h-4 w-4 text-[#1B365D]" />
            <h3 style={HEADING_STACK} className="text-sm font-semibold uppercase tracking-wider">
              Recently updated
            </h3>
          </div>
          <Card>
            <CardContent className="p-2">
              {recentlyUpdated.length === 0 ? (
                <p className="text-xs text-muted-foreground p-3">Nothing yet.</p>
              ) : (
                recentlyUpdated.map((n) => (
                  <button
                    key={n.id}
                    onClick={() => onPick(n)}
                    className="w-full flex items-center justify-between gap-3 px-3 py-2 rounded-md hover:bg-muted/50 text-left"
                  >
                    <div className="min-w-0">
                      <div className="font-medium text-sm truncate">{n.label}</div>
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                        {n.node_type}
                      </div>
                    </div>
                    <span className="text-[10px] text-muted-foreground tabular-nums">
                      {format(new Date(n.updated_at), "dd/MM/yyyy")}
                    </span>
                  </button>
                ))
              )}
            </CardContent>
          </Card>
        </section>
      </div>

      <section className="mt-8">
        <div className="flex items-center gap-2 mb-3">
          <h3 style={HEADING_STACK} className="text-sm font-semibold uppercase tracking-wider">
            What kinds of things does the graph track?
          </h3>
        </div>
        <div className="flex flex-wrap gap-2">
          {typeCounts.map(([t, c]) => (
            <div
              key={t}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-border bg-muted/40 text-xs"
            >
              <span className="font-medium">{t}</span>
              <span className="text-muted-foreground tabular-nums">{c}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function HowItWorks() {
  const sources = [
    { icon: Mail, name: "Gmail", desc: "every 15 min — emails tagged for research" },
    { icon: Calendar, name: "Google Calendar", desc: "hourly — meetings and agendas" },
    { icon: FolderOpen, name: "Google Drive", desc: "every 30 min — documents and decks" },
    { icon: FileText, name: "Google Sheets", desc: "hourly — structured data tabs" },
    { icon: FileText, name: "Notion", desc: "hourly — internal pages and DBs" },
    { icon: FileText, name: "Asana", desc: "hourly — task descriptions and comments" },
    { icon: Mic, name: "Zoom (recordings, AI, chat)", desc: "every 30–60 min — call transcripts" },
    { icon: Mic, name: "Otter", desc: "every 15 min — meeting transcripts" },
    { icon: Database, name: "FRED", desc: "daily 06:00 — macro time series" },
    { icon: Globe, name: "Firecrawl watch rules", desc: "every 15 min — external web pages" },
  ];

  return (
    <Collapsible className="mb-6 border border-border rounded-lg bg-muted/30">
      <CollapsibleTrigger className="w-full flex items-center justify-between gap-2 p-4 text-left hover:bg-muted/50 rounded-lg">
        <div className="flex items-center gap-2">
          <Info className="h-4 w-4 text-[#1B365D]" />
          <span style={HEADING_STACK} className="text-sm font-semibold">
            How the Knowledge Graph works
          </span>
        </div>
        <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform [&[data-state=open]]:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent className="px-4 pb-4 space-y-5">
        <p className="text-sm text-muted-foreground">
          The KG turns everything the research system ingests into a network of <strong>entities</strong> (funds, people, themes, sectors, tickers) and <strong>claims</strong> (sourced relationships between them). It's what powers the research chat, drift detection, and capsule auto-merge.
        </p>

        {/* Pipeline */}
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Pipeline</div>
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <PipelineStep icon={Globe} label="Connectors" sub="pull raw items" />
            <PipelineArrow />
            <PipelineStep icon={FileText} label="Sources" sub="cleaned + chunked" />
            <PipelineArrow />
            <PipelineStep icon={Brain} label="Extraction" sub="LLM finds entities + claims" />
            <PipelineArrow />
            <PipelineStep icon={Network} label="Nodes + Edges" sub="this page" />
            <PipelineArrow />
            <PipelineStep icon={GitBranch} label="Consumed by" sub="chat / drift / capsules" />
          </div>
        </div>

        {/* Connectors */}
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
            Where the data comes from
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {sources.map((s) => (
              <div key={s.name} className="flex items-start gap-2 p-2 rounded-md bg-background border border-border">
                <s.icon className="h-3.5 w-3.5 mt-0.5 text-[#3D8C7C] flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-xs font-medium truncate">{s.name}</div>
                  <div className="text-[10px] text-muted-foreground">{s.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* What happens here */}
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
            What this page lets you do
          </div>
          <ul className="text-xs text-muted-foreground space-y-1 list-disc pl-5">
            <li><strong className="text-foreground">Search an entity</strong> (e.g. "Powell", "Fundsmith") to see every claim that mentions it and where the claim came from.</li>
            <li><strong className="text-foreground">Pivot</strong> from one entity to another by clicking any linked claim — explore the graph by hopping along edges.</li>
            <li><strong className="text-foreground">Check Health</strong> (top right) to see low-confidence edges, orphan nodes, and per-extractor quality.</li>
            <li>The KG <strong className="text-foreground">embeds new nodes every 30 minutes</strong> so semantic search stays current.</li>
          </ul>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function PipelineStep({ icon: Icon, label, sub }: { icon: any; label: string; sub: string }) {
  return (
    <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-background border border-border">
      <Icon className="h-3.5 w-3.5 text-[#1B365D]" />
      <div>
        <div className="text-xs font-medium leading-tight">{label}</div>
        <div className="text-[10px] text-muted-foreground leading-tight">{sub}</div>
      </div>
    </div>
  );
}

function PipelineArrow() {
  return <ArrowRight className="h-3 w-3 text-muted-foreground flex-shrink-0" />;
}


function EntityDetail({
  node,
  edges,
  nodeMap,
  onPivot,
  onClose,
}: {
  node: Node;
  edges: Edge[];
  nodeMap: Map<string, Node>;
  onPivot: (n: Node) => void;
  onClose: () => void;
}) {
  const outgoing = edges.filter((e) => e.from_node_id === node.id);
  const incoming = edges.filter((e) => e.to_node_id === node.id);

  return (
    <div>
      {/* Entity header */}
      <div className="px-6 py-5 border-b border-border bg-muted/20">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="outline" className="border-[#1B365D]/30 text-[#1B365D] bg-[#1B365D]/5">
                {node.node_type}
              </Badge>
              {node.external_table && (
                <span className="text-[11px] font-mono text-muted-foreground">
                  {node.external_table} / {node.external_id?.slice(0, 12)}
                </span>
              )}
            </div>
            <h2 style={HEADING_STACK} className="text-2xl font-semibold truncate">
              {node.label}
            </h2>
            <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
              <span>{outgoing.length} outgoing claims</span>
              <span>·</span>
              <span>{incoming.length} incoming claims</span>
              <span>·</span>
              <span>Updated {format(new Date(node.updated_at), "dd/MM/yyyy")}</span>
            </div>
          </div>
          <div className="flex gap-2">
            <Button asChild variant="outline" size="sm">
              <a href={`/partner/research/kg/${node.id}`}>
                <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Open full page
              </a>
            </Button>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 lg:divide-x divide-border">
        {/* Outgoing */}
        <ClaimsColumn
          title="What this entity links to"
          icon={<ArrowRight className="h-4 w-4 text-[#3D8C7C]" />}
          edges={outgoing}
          direction="out"
          nodeMap={nodeMap}
          onPivot={onPivot}
        />
        {/* Incoming */}
        <ClaimsColumn
          title="What links to this entity"
          icon={<ArrowLeft className="h-4 w-4 text-[#1B365D]" />}
          edges={incoming}
          direction="in"
          nodeMap={nodeMap}
          onPivot={onPivot}
        />
        {/* Properties */}
        <div className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <h3 style={HEADING_STACK} className="text-xs font-semibold uppercase tracking-wider">
              Properties
            </h3>
          </div>
          {node.properties && Object.keys(node.properties).length > 0 ? (
            <div className="space-y-2">
              {Object.entries(node.properties).map(([k, v]) => (
                <div key={k} className="text-xs">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{k}</div>
                  <div className="font-mono break-words text-foreground">
                    {typeof v === "object" ? JSON.stringify(v) : String(v)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">No properties.</p>
          )}
          {node.embedding_model && (
            <div className="mt-4 pt-3 border-t border-border text-[10px] text-muted-foreground">
              Embedded with <span className="font-mono">{node.embedding_model}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ClaimsColumn({
  title,
  icon,
  edges,
  direction,
  nodeMap,
  onPivot,
}: {
  title: string;
  icon: React.ReactNode;
  edges: Edge[];
  direction: "in" | "out";
  nodeMap: Map<string, Node>;
  onPivot: (n: Node) => void;
}) {
  return (
    <div className="p-5">
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <h3 style={HEADING_STACK} className="text-xs font-semibold uppercase tracking-wider">
          {title}
        </h3>
        <span className="text-[10px] text-muted-foreground tabular-nums ml-auto">{edges.length}</span>
      </div>
      {edges.length === 0 ? (
        <p className="text-xs text-muted-foreground">No claims.</p>
      ) : (
        <div className="space-y-2">
          {edges.slice(0, 50).map((e) => {
            const otherId = direction === "out" ? e.to_node_id : e.from_node_id;
            const other = nodeMap.get(otherId);
            return (
              <button
                key={e.id}
                onClick={() => other && onPivot(other)}
                disabled={!other}
                className="w-full text-left p-2.5 rounded-md border border-border hover:border-[#3D8C7C] hover:bg-muted/30 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="secondary" className="text-[10px] py-0">
                    {e.edge_type}
                  </Badge>
                  {e.weight != null && (
                    <span className="text-[10px] text-muted-foreground tabular-nums">
                      w {Number(e.weight).toFixed(2)}
                    </span>
                  )}
                </div>
                <div className="text-sm font-medium truncate">
                  {other?.label ?? (
                    <span className="text-muted-foreground italic text-xs">{nodeLabel(null, otherId)}</span>
                  )}
                </div>
                {other && (
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    {other.node_type}
                  </div>
                )}
              </button>
            );
          })}
          {edges.length > 50 && (
            <p className="text-[10px] text-muted-foreground">Showing first 50 of {edges.length}.</p>
          )}
        </div>
      )}
    </div>
  );
}
